import React, { useState, useEffect } from 'react';
import { Venue } from '../../types';
import { api } from '../../services/api';

interface VenueManagerProps {
  onUpdate?: () => void;
}

const VenueManager: React.FC<VenueManagerProps> = ({ onUpdate }) => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: {
      street: '',
      city: 'Austin',
      state: 'TX',
      zip: ''
    },
    notes: '',
    defaultImageUrl: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  useEffect(() => {
    loadVenues();
  }, []);

  const loadVenues = async () => {
    try {
      const data = await api.getVenues();
      setVenues(data);
    } catch (error) {
      console.error('Failed to load venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingVenue) {
        await api.updateVenue(editingVenue.id, formData);
      } else {
        const venueToCreate = {
          ...formData,
          id: formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          contacts: []
        };
        await api.createVenue(venueToCreate);
      }

      setIsFormOpen(false);
      setEditingVenue(null);
      resetForm();
      await loadVenues();
      onUpdate?.();
    } catch (error) {
      console.error('Failed to save venue:', error);
      alert('Failed to save venue');
    }
  };

  const handleEdit = (venue: Venue) => {
    setEditingVenue(venue);
    setFormData({
      name: venue.name,
      address: venue.address || { street: '', city: 'Austin', state: 'TX', zip: '' },
      notes: venue.notes || '',
      defaultImageUrl: venue.defaultImageUrl || ''
    });
    setImagePreview(venue.defaultImageUrl || null);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this venue? This won\'t affect existing shows.')) {
      try {
        await api.deleteVenue(id);
        await loadVenues();
        onUpdate?.();
      } catch (error) {
        console.error('Failed to delete venue:', error);
        alert('Failed to delete venue');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: {
        street: '',
        city: 'Austin',
        state: 'TX',
        zip: ''
      },
      notes: '',
      defaultImageUrl: ''
    });
    setImagePreview(null);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingVenue(null);
    resetForm();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result as string;

        try {
          const data = await api.uploadImage(base64);
          setFormData({ ...formData, defaultImageUrl: data.url });
          setImagePreview(data.url);
        } catch (err) {
          alert('Failed to upload image');
        } finally {
          setUploadingImage(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
      setUploadingImage(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading venues...</div>;
  }

  return (
    <div>
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-medium">Venues</h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage venue information for quick selection when creating shows
              </p>
            </div>
            <button
              onClick={() => setIsFormOpen(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Add Venue
            </button>
          </div>
        </div>

        <div className="divide-y divide-gray-200">
          {venues.length === 0 ? (
            <p className="px-6 py-4 text-gray-500">
              No venues added yet. Add venues for quick selection when creating shows.
            </p>
          ) : (
            venues.map(venue => (
              <div key={venue.id} className="px-6 py-4">
                <div className="flex justify-between items-start">
                  <div className="flex space-x-4">
                    {venue.defaultImageUrl && (
                      <img
                        src={venue.defaultImageUrl}
                        alt={`${venue.name} default`}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {venue.name}
                      </h3>
                      {venue.address && (
                        <p className="text-sm text-gray-500">
                          {venue.address.street && `${venue.address.street}, `}
                          {venue.address.city}, {venue.address.state} {venue.address.zip}
                        </p>
                      )}
                      {venue.notes && (
                        <p className="text-sm text-gray-600 mt-1">{venue.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(venue)}
                      className="text-blue-600 hover:text-blue-900 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(venue.id)}
                      className="text-red-600 hover:text-red-900 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Venue Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium">
                {editingVenue ? 'Edit Venue' : 'Add New Venue'}
              </h3>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Venue Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="The Saxon Pub"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  value={formData.address.street}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, street: e.target.value }
                  })}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="1320 S Lamar Blvd"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.address.city}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, city: e.target.value }
                    })}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Austin"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.address.state}
                    onChange={(e) => setFormData({
                      ...formData,
                      address: { ...formData.address, state: e.target.value }
                    })}
                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    placeholder="TX"
                    maxLength={2}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={formData.address.zip}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: { ...formData.address, zip: e.target.value }
                  })}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="78704"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Parking info, load-in instructions, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Venue Image (optional)
                </label>
                <div className="space-y-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {uploadingImage && (
                    <p className="text-sm text-gray-500">Uploading image...</p>
                  )}
                  <p className="text-xs text-gray-500">
                    This image will be used as the default for shows at this venue. Can be overridden per show.
                  </p>

                  {/* Large image preview at the bottom */}
                  {imagePreview && (
                    <div className="mt-4">
                      <div className="text-sm font-medium text-gray-700 mb-2">Current Image:</div>
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Venue default preview"
                          className="w-full h-64 object-cover rounded-md border border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({ ...formData, defaultImageUrl: '' });
                            setImagePreview(null);
                          }}
                          className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 shadow-lg"
                          title="Remove image"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingVenue ? 'Update' : 'Add'} Venue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueManager;
